// @flow
import * as React from 'react';
import { Box } from '@mui/material';
import { FormikDateInput, FormikTextInput } from '@coverright/ui/inputs';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { forwardRef } from 'react';
import { InputMask, InputMaskProps } from '@react-input/mask';

type Props = {
  initial: MedicareFormFields;
  loading?: boolean;
  slots?: {
    helper?: React.ReactNode;
    medicareIcon?: React.ReactNode;
  };
  onSubmit: (data: MedicareFormFields) => void;
  theme?: 'light' | 'dark';
};

const validationSchema = {
  medicareNumber: yup
    .string()
    .nullable()
    .default(''),
  eligibleName: yup
    .string()
    .nullable()
    .default(''),
  partADate: yup
    .string()
    .nullable()
    .default(''),
  partBDate: yup
    .string()
    .nullable()
    .default(''),
};

export function MedicareCardForm(props: Props) {
  const formik = useFormik({
    initialValues: props.initial,
    validationSchema: yup.object(validationSchema),
    onSubmit: (values) => {
      if (formik.isValid) {
        props.onSubmit(values);
      }
    },
    validateOnChange: false,
  });

  const color: any = props.theme !== 'dark' ? 'white' : 'primary';

  return (
    <Box
      component={'form'}
      data-test={'medicare-card-form'}
      onSubmit={formik.handleSubmit}
      sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <FormikTextInput
        formik={formik}
        name={'eligibleName'}
        labelProps={{ sx: { color: '#9EA5B1!important' } }}
        label={'Full legal name'}
        placeholder={'Enter full legal name'}
        color={color}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <FormikTextInput
          formik={formik}
          name={'medicareNumber'}
          labelProps={{ sx: { color: '#9EA5B1!important' } }}
          InputProps={{
            inputComponent: MedicareNumberInputMask,
          }}
          placeholder={'Enter number here'}
          color={color}
        />
        {props.slots?.medicareIcon}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <FormikDateInput
          formik={formik}
          name={'partADate'}
          label={'Part A Coverage starts'}
          sx={{'& .MuiInputAdornment-positionEnd button': {color: props.theme !== 'dark' ? '#FFF!important' : undefined} }}
          labelProps={{ sx: { color: '#9EA5B1!important' } }}
          color={color}
        />
        <FormikDateInput
          formik={formik}
          name={'partBDate'}
          label={'Part B Coverage starts'}
          sx={{'& .MuiInputAdornment-positionEnd button': {color: props.theme !== 'dark' ? '#FFF!important' : undefined} }}
          labelProps={{ sx: { color: '#9EA5B1!important' } }}
          color={color}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: props.slots?.helper ? 'space-between' : 'flex-end',
          gap: 2,
          width: 1
        }}
      >
        {props.slots?.helper}
        <LoadingButton
          type={'submit'}
          loading={props.loading}
          id={'medicare-card-save-button'}
          data-test={'medicare-card-save-button'}
          color={color}
          variant={'outlined'}
        >
          Save
        </LoadingButton>
      </Box>
    </Box>
  );
}

const MedicareNumberInputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  (props, forwardedRef) => (
    <InputMask
      ref={forwardedRef}
      mask="____-___-____"
      replacement={{ _: /[0-9a-zA-Z]/ }}
      {...props}
    />
  )
);

export interface MedicareFormFields {
  medicareNumber?: string | null;
  partADate?: string | null;
  partBDate?: string | null;
  eligibleName?: string | null;
}
